import { icon, refreshIcons } from '../icons';
import { listTransactions, addExpensesBulk, listUploads, removeUpload, type UploadSummary } from '@/storage/repo';
import { CATEGORY_BADGE, FALLBACK_BADGE, formatZAR } from '../format';
import { appState } from '../state';

export function renderReview(root: HTMLElement) {
  root.innerHTML = `
    <main class="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div class="flex items-center gap-3">
        ${icon('clipboard-check', 'w-5 h-5 text-indigo-500')}
        <h2 class="text-xl font-semibold">Imported transactions</h2>
      </div>
      <div id="reviewBody" class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 p-6 text-sm text-slate-500">
        Loading…
      </div>
    </main>
  `;
  refreshIcons();
  void run();
}

async function run() {
  const body = document.getElementById('reviewBody')!;
  const [txs, uploads] = await Promise.all([listTransactions(), listUploads()]);

  const uploadsCard = uploads.length ? `
    <section class="mb-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-5">
      <div class="flex items-center gap-2 mb-3">
        ${icon('files', 'w-4 h-4 text-slate-500')}
        <h3 class="font-semibold text-slate-700 dark:text-slate-200">Uploaded statements</h3>
        <span class="text-xs text-slate-400">${uploads.length} file${uploads.length === 1 ? '' : 's'}</span>
      </div>
      <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">Removing a statement deletes its raw rows from your device. Expenses you already added to a budget are not touched.</p>
      <ul class="divide-y divide-slate-100 dark:divide-slate-800">
        ${uploads.map(u => uploadRow(u)).join('')}
      </ul>
    </section>
  ` : '';

  if (txs.length === 0) {
    body.innerHTML = `
      <p class="text-center py-6">No transactions imported yet. <a href="#/upload" class="text-indigo-600 dark:text-indigo-400 font-semibold">Upload a statement</a> to populate this view.</p>
    `;
    return;
  }

  // Group by month
  const byMonth = new Map<string, typeof txs>();
  for (const t of txs) {
    const ym = t.postedAt.slice(0, 7);
    if (!byMonth.has(ym)) byMonth.set(ym, []);
    byMonth.get(ym)!.push(t);
  }
  const months = [...byMonth.keys()].sort().reverse();
  const currentMonth = appState.get().currentMonth;

  body.innerHTML = `
    ${uploadsCard}
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      ${txs.length} transaction${txs.length === 1 ? '' : 's'} across ${months.length} month${months.length === 1 ? '' : 's'}.
      Tick rows to add them to your <strong>${currentMonth}</strong> budget.
    </p>
    ${months.map(ym => {
      const items = byMonth.get(ym)!;
      const total = items.reduce((s, t) => s + Math.abs(t.amountCents), 0) / 100;
      return `
        <details class="mb-3 rounded-xl border border-slate-200 dark:border-slate-800" open>
          <summary class="px-4 py-3 flex items-center justify-between cursor-pointer">
            <span class="font-semibold">${ym}</span>
            <span class="text-xs text-slate-500 dark:text-slate-400">${items.length} rows · ${formatZAR(total)} debited</span>
          </summary>
          <ul class="divide-y divide-slate-100 dark:divide-slate-800">
            ${items.map((t, i) => row(t, `${ym}-${i}`)).join('')}
          </ul>
        </details>
      `;
    }).join('')}
    <div class="mt-5 flex items-center gap-3">
      <button id="addSelectedBtn" class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Add selected to ${currentMonth}</button>
      <span id="addStatus" class="text-xs text-slate-500"></span>
    </div>
  `;
  refreshIcons();

  // Wire upload removal
  document.querySelectorAll<HTMLButtonElement>('.js-remove-upload').forEach(btn => {
    btn.addEventListener('click', async () => {
      const file = btn.dataset.file!;
      const count = Number(btn.dataset.count || 0);
      if (!confirm(`Remove "${file}" and its ${count} transaction${count === 1 ? '' : 's'} from this device?`)) return;
      await removeUpload(file);
      await run(); // re-render
    });
  });

  document.getElementById('addSelectedBtn')?.addEventListener('click', async () => {
    const checks = document.querySelectorAll<HTMLInputElement>('.js-tx:checked');
    const ids = new Set([...checks].map(c => c.value));
    const items = txs
      .filter(t => ids.has(t.id))
      .map(t => ({
        ym: currentMonth,
        name: prettyName(t.description),
        category: (t.category as any) || 'Living Allocations',
        amount: Math.abs(t.amountCents) / 100,
        notes: `Imported from ${t.bank} statement`,
        source: 'review',
      }));
    if (items.length === 0) return;
    await addExpensesBulk(items);
    const status = document.getElementById('addStatus')!;
    status.textContent = `Added ${items.length} item${items.length === 1 ? '' : 's'} to ${currentMonth}.`;
  });
}

function uploadRow(u: UploadSummary): string {
  const banks = u.banks.map(b => b.charAt(0).toUpperCase() + b.slice(1)).join(', ') || 'Unknown bank';
  const period = u.firstPosted
    ? `${u.firstPosted}${u.lastPosted && u.lastPosted !== u.firstPosted ? ` – ${u.lastPosted}` : ''}`
    : 'no dates';
  return `
    <li class="py-3 flex items-center gap-3">
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">${escapeHtml(u.sourceFile)}</div>
        <div class="text-xs text-slate-500 dark:text-slate-400">
          ${escapeHtml(banks)} · ${u.count} transaction${u.count === 1 ? '' : 's'} · ${escapeHtml(period)} · imported ${escapeHtml((u.importedAt || '').slice(0, 10))}
        </div>
      </div>
      <button
        class="js-remove-upload inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-rose-200 text-rose-700 dark:border-rose-700/50 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition"
        data-file="${escapeHtml(u.sourceFile)}"
        data-count="${u.count}"
      >
        ${icon('trash-2', 'w-3.5 h-3.5')} Remove
      </button>
    </li>
  `;
}

function row(t: any, key: string) {
  const amount = (Math.abs(t.amountCents) / 100).toFixed(2);
  const badge = CATEGORY_BADGE[t.category || ''] || FALLBACK_BADGE;
  return `
    <li class="px-4 py-2 flex items-center gap-3 hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
      <input type="checkbox" class="js-tx nice-check" value="${escapeHtml(t.id)}" id="tx-${escapeHtml(key)}" />
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">${escapeHtml(t.description)}</div>
        <div class="text-xs text-slate-500 dark:text-slate-400">${escapeHtml(t.postedAt)} · ${escapeHtml(t.bank)}</div>
      </div>
      <span class="px-2 py-0.5 text-[10px] font-semibold rounded-full ${t.category ? badge : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 ring-1 ring-slate-200/60 dark:ring-slate-700/40'}">${escapeHtml(t.category || 'uncategorised')}</span>
      <div class="text-right text-sm tabular-nums">R ${amount}</div>
    </li>
  `;
}

function prettyName(desc: string): string {
  return desc.replace(/\s+/g, ' ').trim().slice(0, 64);
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
